import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { GanttChartSquare, Users, Sparkles, ShieldCheck, KanbanSquare, LineChart, ArrowRight } from "lucide-react";

const FEATURES = [
  { icon: GanttChartSquare, title: "Gantt, WBS & Critical Path", text: "See the real path to done. Drag bars, set baselines, watch slippage, and let the critical path show you exactly what matters." },
  { icon: KanbanSquare, title: "Kanban with Swimlanes", text: "Boards that stay honest. Group by person or phase — always in sync with the Gantt, never a separate source of truth." },
  { icon: Sparkles, title: "AI Copilot", text: "Your project has a memory. Chat with it, draft charters and status reports, surface risks, and check go-live readiness without starting from zero." },
  { icon: Users, title: "Teams & Accountability", text: "Nine clear roles, easy invites, owners and approvers on every item. Workload warnings and escalations so nothing quietly slips." },
  { icon: LineChart, title: "Full Delivery Toolkits", text: "PMBOK, Agile, Lean and Six Sigma in one place. RAID, requirements, change, FMEA, Gauge R&R, benefits and PIR — ready when you need them." },
  { icon: ShieldCheck, title: "Built for real organisations", text: "Strict tenant isolation, portfolio views, subscriptions that just work, and exports when you need to share outside the tool." },
];

export default function HomePage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="home-page">
      <header className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <img src="/logo-icon.png" alt="Ganttastic" className="h-10 w-10" />
          <span className="font-display font-black tracking-tighter text-xl">Ganttastic<span className="text-[#5B93F0]">.app</span></span>
        </div>
        <nav className="flex items-center gap-4">
          <Link data-testid="home-pricing-link" to="/pricing" className="text-sm text-neutral-300 hover:text-white">Pricing</Link>
          {user && user !== false ? (
            <Link to="/projects"><Button data-testid="goto-app-btn" className="rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]">Go to app</Button></Link>
          ) : (
            <>
              <Link data-testid="home-login-link" to="/login" className="text-sm text-neutral-300 hover:text-white underline">Log in</Link>
              <Link to="/signup"><Button data-testid="home-signup-btn" className="rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]">Create account</Button></Link>
            </>
          )}
        </nav>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-16 pb-20">
        <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter leading-none max-w-3xl">
          Project management<br />just got <span className="text-[#5B93F0]">Ganttastic.</span>
        </h1>
        <p className="mt-6 text-neutral-400 max-w-xl text-base leading-relaxed">
          The clarity you've been missing, without the usual weight that comes with it. Plans stay honest, teams stay aligned, and progress finally feels possible — even when things get complicated.
        </p>
        <div className="mt-8 flex gap-3">
          <Link to="/signup"><Button data-testid="hero-signup-btn" className="rounded-none bg-[#1E6FE8] hover:bg-[#1557C6] px-6 py-6 text-base">Start free 14-day trial <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
          <Link to="/login"><Button data-testid="hero-login-btn" variant="outline" className="rounded-none border-neutral-700 text-neutral-200 bg-transparent hover:bg-neutral-900 hover:text-white px-6 py-6 text-base">Log in</Button></Link>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-20" data-testid="home-screenshots">
        <div className="overline-label text-neutral-500 mb-4">The real product — a live Gantt with critical path, and a dashboard that tells the truth.</div>
        <div className="border border-neutral-800 bg-neutral-900">
          <div className="flex items-center gap-1.5 px-3 py-2 border-b border-neutral-800">
            <span className="h-2.5 w-2.5 rounded-full bg-neutral-700" /><span className="h-2.5 w-2.5 rounded-full bg-neutral-700" /><span className="h-2.5 w-2.5 rounded-full bg-neutral-700" />
            <span className="text-[10px] text-neutral-600 ml-2 font-mono">ganttastic.app — WBS / Gantt / Critical Path</span>
          </div>
          <img src="/shot-gantt.png" alt="Ganttastic Gantt chart with critical path" className="w-full block" loading="lazy" />
        </div>
        <div className="grid md:grid-cols-5 gap-4 mt-4 items-start">
          <div className="md:col-span-3 border border-neutral-800 bg-neutral-900">
            <div className="flex items-center gap-1.5 px-3 py-2 border-b border-neutral-800">
              <span className="h-2.5 w-2.5 rounded-full bg-neutral-700" /><span className="h-2.5 w-2.5 rounded-full bg-neutral-700" /><span className="h-2.5 w-2.5 rounded-full bg-neutral-700" />
              <span className="text-[10px] text-neutral-600 ml-2 font-mono">ganttastic.app — Project Dashboard</span>
            </div>
            <img src="/shot-dashboard.png" alt="Ganttastic project dashboard with RAG status and milestones" className="w-full block" loading="lazy" />
          </div>
          <div className="md:col-span-2 pt-2">
            <h3 className="font-display font-bold tracking-tight text-2xl leading-tight">One source of truth,<br />from task to portfolio.</h3>
            <p className="text-sm text-neutral-400 mt-3 leading-relaxed">
              Drag a bar on the Gantt and the critical path recomputes instantly. RAG status, milestones, escalations and budget roll straight up to your dashboard — and across every project in your portfolio.
            </p>
            <Link to="/pricing" className="inline-flex items-center gap-1.5 text-sm text-[#5B93F0] hover:text-white mt-4" data-testid="home-see-pricing-link">
              See plans & pricing <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-20 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {FEATURES.map((f) => (
          <div key={f.title} className="border border-neutral-800 bg-neutral-900/50 p-5">
            <f.icon className="h-5 w-5 text-[#5B93F0]" />
            <h3 className="font-display font-bold tracking-tight mt-3">{f.title}</h3>
            <p className="text-sm text-neutral-400 mt-1.5 leading-relaxed">{f.text}</p>
          </div>
        ))}
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="flex items-center justify-between mb-4">
          <div className="overline-label text-neutral-500">Plans — every new organisation starts with a free 14-day trial.</div>
          <Link to="/pricing" className="text-sm text-[#5B93F0] hover:text-white underline" data-testid="home-full-pricing-link">Full pricing →</Link>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {plans.filter((p) => p.interval !== "year").map((p) => (
            <div key={p.code} className="border border-neutral-800 bg-neutral-900/50 p-5">
              <div className="overline-label text-neutral-500">{p.name}</div>
              <div className="font-display font-black text-3xl tracking-tighter mt-1">${p.price}<span className="text-sm font-normal text-neutral-500">/mo</span></div>
              <ul className="text-xs text-neutral-400 space-y-1 mt-3">
                <li>{p.maxProjects >= 9999 ? "Unlimited" : p.maxProjects} projects · {p.maxUsers >= 9999 ? "unlimited" : p.maxUsers} users · min {p.minSeats} seats</li>
                <li>AI assistant: {p.aiEnabled ? "included" : "—"}</li>
                <li>Six Sigma & Lean toolkits: {p.sixSigmaEnabled ? "included" : "—"}</li>
              </ul>
              <Link to={`/signup?plan=${p.code}`}>
                <Button data-testid={`home-plan-${p.code}-btn`} className="rounded-none w-full mt-4 bg-[#1E6FE8] hover:bg-[#1557C6]">Choose {p.name}</Button>
              </Link>
            </div>
          ))}
        </div>
      </section>
      <footer className="border-t border-neutral-800 py-6 text-center text-xs text-neutral-600">ganttastic.app · Plan. Align. Deliver.</footer>
    </div>
  );
}
